import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.scss";
import { ActiveStepStore } from "src/store/onboarding.store";
import imageVid from "src/assets/img/imageVid.png";
import { useSectionStore } from "src/store/croModule.state";
import { useMarketStore } from "src/store/market.state";
import Checked from "../../assets/icons/CheckCircleIcon.svg";
import LightBulb from "../../assets/icons/Lightbulb.svg";
import { getMaterialByStep } from "./utils";
import { StepItem } from "./types";
import Lottie from "lottie-react";
import cogsAnimation from "src/assets/COGS_anim.json";

const LearningMaterials = ({
  activeStep,
  setModal,
}: {
  activeStep: number;
  setModal: (arg0: boolean) => void;
}) => {
  const [visible, setVisible] = useState(true);
  const navigate = useNavigate();

  const {
    setStep,
    partOneOne,
    partOneTwo,
    partThreeOne,
    partThreeTwo,
    setPartOneTwo,
    setPartThreeOne,
    setPartThreeTwo,
  } = ActiveStepStore();

  const { isWhatIf, changeWhatIf } = useSectionStore((state) => ({
    isWhatIf: state.isWhatIf,
    changeWhatIf: state.changeWhatIf,
  }));

  const { setIsWhatIf } = useMarketStore();

  // Effect to reset visibility when active step changes
  useEffect(() => {
    setVisible(true);
  }, [activeStep]);

  // Handlers for different step actions
  const handleConnectAccount = () => {
    if (!partOneOne) {
      navigate("/connect-seller-account");
    } else if (partOneOne && !partOneTwo) {
      window.location.href = "https://advertising.amazon.com/sign-in";
      setPartOneTwo(true);
      setStep(2);
    }
  };

  const handleMarketIntelligence = () => {
    if (!partThreeOne) {
      setPartThreeOne(true);
    } else if (partThreeOne && !partThreeTwo) {
      setIsWhatIf(true);
      changeWhatIf(!isWhatIf);
      setPartThreeTwo(true);
      setStep(4);
    }
  };

  // Main action handler based on active step
  const handleAction = () => {
    switch (activeStep) {
      case 1:
        handleConnectAccount();
        break;
      case 2:
        setModal(true);
        break;
      case 3:
        handleMarketIntelligence();
        break;
      case 4:
        navigate("/canvas", { state: {} });
        break;
    }
  };

  // Compute button label based on current step and progress
  const getButtonLabel = () => {
    if (activeStep === 1) return "CONNECT";
    if (activeStep === 2) return "ADD COGS";

    if (activeStep === 3) {
      if (!partThreeOne) return "CHECK MI";
      if (partThreeOne && !partThreeTwo) return "SWITCH TO WHAT IF";
    }

    if (activeStep === 4) return "GO TO CANVAS";
    return "CONTINUE";
  };

  // Compute step progress for displaying completion indicators
  const getStepProgress = () => {
    const progressMap: Record<number, { completed: number; total: number }> = {
      1: {
        completed: [partOneOne, partOneTwo].filter(Boolean).length,
        total: 2,
      },
      3: {
        completed: [partThreeOne, partThreeTwo].filter(Boolean).length,
        total: 2,
      },
    };

    return progressMap[activeStep] || { completed: 0, total: 0 };
  };

  const getStepTitle = () => {
    switch (activeStep) {
      case 1:
        return "Connect Amazon Accounts";
      case 2:
        return "Add COGS to get ASIN Level ROI";
      case 3:
        return "Market intelligence: how to setup market share goal to grow your business";
      default:
        return "Setup Market Share goal";
    }
  };

  const getStepItems = (): StepItem[] => {
    if (activeStep === 2) return [];
    const stepProgress = getStepProgress();

    const getFirstItemTitle = () => {
      if (activeStep === 1) return "Connect Amazon Seller Account";
      if (activeStep === 4) return "Start defining your market share goals";
      return "Check Market Intelligence Insights";
    };

    const firstItem: StepItem = {
      completed: stepProgress.completed >= 1,
      title: getFirstItemTitle(),
      description: getFirstItemDescription(),
    };

    const getSecondItemTitle = () => {
      if (activeStep === 1) return "Connect Amazon Ads Account";
      if (activeStep === 4) return "Visualize your performance";
      return "Try what if scenarios";
    };

    const secondItem: StepItem = {
      completed: stepProgress.completed >= 2,
      title: getSecondItemTitle(),
      description: getSecondItemDescription(),
    };

    const items = [firstItem, secondItem];

    // Add third item for step 4
    if (activeStep === 4) {
      items.push({
        completed: false,
        title: "Track goal over time",
        description:
          "Monitor how your campaigns contribute to reaching your market share targets, across regions and products.",
      });
    }

    return items;
  };

  // Helper functions for content generation
  const getFirstItemDescription = () => {
    if (activeStep === 1) {
      return "Enables data sync for product performance, sales, and inventory from Amazon Seller Central.";
    }

    if (activeStep === 4) {
      return "Set a target value and timeframe to start tracking your share across Amazon marketplaces.";
    }

    return (
      <div className={styles.material}>
        <p>
          <span>Elevate Your Business Strategy with Market Intelligence!</span>
        </p>
        <p>
          <span>
            Many sellers focus narrowly on ACoS targets without considering
            broader market trends, competitor strategies, or long-term business
            goals. This can lead to missed opportunities, inefficient ad spend,
            and suboptimal growth.
          </span>
        </p>
        <p>
          <span>
            Market Intelligence refers to data-driven insights about your
            competitors, sales velocity including ad sales and organic sales,
            and market trends. It helps you understand the "big picture" of your
            niche and make informed decisions.
          </span>
        </p>
      </div>
    );
  };

  const getSecondItemDescription = () => {
    if (activeStep === 1) {
      return "Track your advertising performance across campaigns directly in the dashboard.";
    }

    if (activeStep === 2) return null;

    if (activeStep === 4) {
      return "Review goals across ASINs and categories with charts and forecasts.";
    }

    return (
      <div className={styles.material}>
        <p>
          <span>
            The "WHAT IF" mode is a powerful forecasting tool designed to help
            you explore different scenarios and make data-driven decisions. By
            adjusting market share goals, you can visually analyze the
            correlation between your market model, sales performance, product
            metrics, and high-level business metrics. This feature allows you to
            experiment with various market share values for a specified future
            period and observe how these changes impact key parameters.
          </span>
        </p>
        <p>Tips for Effective Use</p>
        <ul>
          <li>
            Start Small : Begin with minor adjustments to market share goals to
            understand the immediate effects.
          </li>
          <li>
            Combine Metrics : Look at multiple metrics simultaneously to get a
            holistic view of the impact.
          </li>
          <li>
            Save Scenarios : Save your "WHAT IF" scenarios for future reference
            or to compare results over time.
          </li>
          <li>
            Collaborate : Share your findings with your team to align on
            strategies and next steps.
          </li>
        </ul>
      </div>
    );
  };

  // Check if we should render content for this step
  if (![1, 2, 3, 4].includes(activeStep)) return null;

  const buttonLabel = getButtonLabel();
  const stepItems = getStepItems();

  return (
    <div className={styles.wrapper}>
      <button className={styles.toggle} onClick={() => setVisible(!visible)}>
        {!visible && (
          <img src={LightBulb} alt="LightBulb" height={16} width={16} />
        )}
        <span>{!visible && "SHOW LEARNING MATERIALS"}</span>
      </button>

      {visible && (
        <div className={styles.card}>
          {/* Canvas button for step 4 */}
          {activeStep === 4 && (
            <button className={styles.canvasBtn} onClick={handleAction}>
              <ArrowUpRight size={14} />
              {buttonLabel}
            </button>
          )}

          <div className={styles.header}>
            <div className={styles.title}>
              <img src={LightBulb} alt="LightBulb" height={24} width={24} />
              <h3>{getStepTitle()}</h3>
            </div>

            {activeStep !== 4 && (
              <button className={styles.connectBtn} onClick={handleAction}>
                <ArrowUpRight size={14} />
                {buttonLabel}
              </button>
            )}
          </div>

          <div className={styles.cardWrapper}>
            <div className={styles.left}>
              {/* Header section */}

              {/* Step-specific content */}
              {getMaterialByStep(activeStep)}

              {/* Step items */}
              {stepItems.map((item) => (
                <div className={styles.option} key={item.title}>
                  <div className={styles.circle}>
                    {item.completed ? (
                      <img
                        src={Checked}
                        alt="Checked"
                        width={20}
                        height={20}
                        color="#22c55e"
                      />
                    ) : (
                      <div className={styles.circleEmpty} />
                    )}
                  </div>
                  <div>
                    <strong
                      className={
                        item.completed ? styles.crossed : styles.incomplete
                      }
                    >
                      {item.title}
                    </strong>
                    <p
                      className={
                        !item.completed ? styles.incomplete : undefined
                      }
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}

              <button className={styles.ht} onClick={() => setVisible(false)}>
                HIDE LEARNING MATERIALS
              </button>
            </div>

            {/* Image for step 2 and 4 */}
            {activeStep === 2 && (
              <div className={styles.imageWrapper}>
                <Lottie
                  animationData={cogsAnimation}
                  loop={true}
                  autoplay={true}
                  style={{ width: "100%", height: "100%" }}
                />
              </div>
            )}
            {activeStep === 4 && (
              <div className={styles.imageWrapper}>
                <img src={imageVid} alt="Market Share Goal UI" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningMaterials;
